import { Anchor, Box, Flex, Group, Image, Skeleton, Text } from '@mantine/core'
import { type DataTableColumn, type DataTableDefaultColumnProps } from 'mantine-datatable'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import ChevronRightIcon from '@/assets/icons/chevron-right.svg'
import Sparkline from '@/components/chart/Sparkline'
import PriceChange from '@/components/PriceChange'
import { GTM_EVENTS } from '@/configs/dataTrackingEvent'
import { I18nLink } from '@/i18n/navigation'
import { trackGtmEvent } from '@/services/dataTracking'
import type { EquityLatestPrice } from '@/services/equity/prices'
import type { EquityScreenerAsset } from '@/services/equity/screener'
import { type StockSparklineInterval, useStockSparkline } from '@/services/equity/sparkline'
import { formatMarketCap, formatPrice } from '@/utils/number/format'

import styles from '../styles.module.css'

// Placeholder for a missing value in the stocks table: an em dash in the
// secondary content color (matches the Prediction cards' placeholder), instead
// of the global "N/A" string.
function NaPlaceholder() {
  return (
    <Text component="span" c="var(--content-secondary)">
      —
    </Text>
  )
}

export const defaultColumnProps: DataTableDefaultColumnProps<EquityScreenerAsset> = {
  titleClassName: 'typography-body',
  cellsClassName: 'typography-body-number',
}

function AssetColumn({ asset }: { asset: EquityScreenerAsset }) {
  // Upstream returns `icon_urls` as an array of `{ style, url }`; the first entry
  // is the circular icon (verified 2026-09-01). Layout mirrors coinTable's
  // CoinColumn: icon left, then name (primary) with symbol (tertiary).
  const iconUrl = asset.icon_urls?.[0]?.url
  return (
    <Group gap="8px" wrap="nowrap" className={styles.assetGroup}>
      {iconUrl ? (
        <Image src={iconUrl} alt={asset.display_symbol} w={24} h={24} radius="50%" />
      ) : null}
      {/* Symbol stacked under the name below medium, side by side from
          medium up (matches both design frames). It used to be
          `visibleFrom="midScale"`, which hid the ticker outright on mobile and
          tablet — WWW-5196. WWW-5152 moved the inline↔stacked switch down to
          the medium breakpoint so tablet renders the same cell as desktop. */}
      <Flex
        direction={{ base: 'column', medium: 'row' }}
        align={{ base: 'flex-start', medium: 'center' }}
        gap={{ base: 0, medium: 8 }}
        className={styles.assetNameCol}
      >
        <Text
          variant="body"
          c="var(--content-primary)"
          truncate
          maw={{ base: '100%', medium: '250px' }}
          title={asset.name}
        >
          {asset.name}
        </Text>
        <Text
          variant="body"
          c="var(--content-tertiary)"
          truncate
          maw="100px"
          title={asset.display_symbol}
        >
          {asset.display_symbol}
        </Text>
      </Flex>
    </Group>
  )
}

// Price of record: after-hours when the market is closed, regular-hours close
// otherwise (mirrors what the API returns in after_hours_price /
// regular_hours_price).
// Note: the API returns "" (falsy, not null) for a missing phase price, so `??`
// would NO-OP the fallback; use `||` to skip empty strings too. Stock prices
// are never 0, so truthiness is safe here.
const getDisplayPrice = (p?: EquityLatestPrice): number | null => {
  const raw = p?.after_hours_price || p?.regular_hours_price || p?.mid_price
  if (!raw) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

function PriceColumn({ price }: { readonly price?: EquityLatestPrice }) {
  const value = getDisplayPrice(price)
  if (value === null) return <NaPlaceholder />
  return (
    <Text component="span" variant="body-emphasis" className={styles.mobileCellNumber}>
      ${formatPrice(value)}
    </Text>
  )
}

function Change24hColumn({ asset }: { asset: EquityScreenerAsset }) {
  const change = asset.performance
  if (change === null || change === undefined) return <NaPlaceholder />
  // Upstream `performance` is already a percentage number (e.g. "1.14" = +1.14%),
  // but PriceChange expects a decimal fraction and multiplies by 100 internally.
  const asNumber = Number(change)
  if (!Number.isFinite(asNumber)) return <NaPlaceholder />
  return <PriceChange value={asNumber / 100} className={styles.mobileCellNumber} />
}

function MarketCapColumn({ asset }: { asset: EquityScreenerAsset }) {
  const amount = asset.market_cap?.amount
  if (!amount) return <NaPlaceholder />
  const value = Number(amount)
  if (!Number.isFinite(value)) return <NaPlaceholder />
  return <Text component="span">${formatMarketCap(value)}</Text>
}

const sparklineStyles = { w: '66px', h: '24px' } as const
const sparklineSkeleton = <Skeleton {...sparklineStyles} bdrs="40px" />

interface SparklineColumnProps {
  readonly asset: EquityScreenerAsset
  readonly assetIdBySymbol: Record<string, string>
  // '5D' when the probe found the market closed (off-hours/weekend): the
  // whole table switches interval and swaps the header label (decision 2026-09-07).
  readonly interval: StockSparklineInterval
}

function SparklineColumn({ asset, assetIdBySymbol, interval }: SparklineColumnProps) {
  // The screener returns `S_NVDA.O`-style ids; the public equity-market-data
  // chart API needs the NUMERIC asset_id, mapped via the cached assets-preview
  // list (see StockTable/index.tsx).
  const assetId = assetIdBySymbol[asset.display_symbol] ?? ''
  const [inViewport, setInViewport] = useState(false)
  const { data, isLoading } = useStockSparkline(assetId, interval, inViewport && assetId !== '')

  if (!assetId) {
    return (
      <Flex justify="flex-end">
        <NaPlaceholder />
      </Flex>
    )
  }

  return (
    <Flex justify="flex-end">
      <Sparkline
        data={data ?? []}
        isLoading={isLoading}
        {...sparklineStyles}
        fallback={sparklineSkeleton}
        inViewOptions={{ onInView: () => !inViewport && setInViewport(true) }}
        style={{ pointerEvents: 'none' }}
      />
    </Flex>
  )
}

function PriceWithChangeColumn({
  asset,
  price,
}: {
  readonly asset: EquityScreenerAsset
  readonly price?: EquityLatestPrice
}) {
  // Merged price + 24h change in a single cell for mobile/tablet layouts.
  return (
    <Flex direction="column" align="flex-end" gap="2px">
      <PriceColumn price={price} />
      <Change24hColumn asset={asset} />
    </Flex>
  )
}

function ChevronColumn({ asset }: { readonly asset: EquityScreenerAsset }) {
  // The svg asset hardcodes fill="#fff" (invisible on the light theme); theme it
  // via currentColor on the wrapper.
  return (
    <Box
      component={I18nLink}
      href={`/price/stocks/${asset.display_symbol}`}
      c="var(--content-secondary)"
      onClick={e => e.stopPropagation()}
    >
      <ChevronRightIcon width={16} height={16} fill="currentColor" />
    </Box>
  )
}

function TradeColumn({ asset }: { readonly asset: EquityScreenerAsset }) {
  const t = useTranslations('coinTable.row')
  // "Trade" text link → in-app stock detail page（与 mobile 的 chevron 同一目标）。
  return (
    <Anchor
      component={I18nLink}
      href={`/price/stocks/${asset.display_symbol}`}
      onClick={e => {
        e.stopPropagation()
        trackGtmEvent({
          event: GTM_EVENTS.StocksTradeClicked,
          asset_symbol: asset.display_symbol,
        })
      }}
      underline="never"
    >
      <Text variant="subtitle-emphasis" c="var(--content-active)" style={{ wordBreak: 'keep-all' }}>
        {t('trade')}
      </Text>
    </Anchor>
  )
}

export function useStockColumns(
  assetIdBySymbol: Record<string, string>,
  priceByAssetId: Record<string, EquityLatestPrice> | undefined,
  interval: StockSparklineInterval,
) {
  const t = useTranslations('stockTable.headers')
  return useMemo<DataTableColumn<EquityScreenerAsset>[]>(
    () => [
      {
        accessor: 'display_symbol',
        title: t('asset'),
        ellipsis: true,
        cellsClassName: styles.defaultCell,
        render: asset => <AssetColumn asset={asset} />,
      },
      // Tablet/desktop: separate price and 24h-change columns (same as the
      // crypto price page). Visible from medium up — tablet is not treated
      // specially; only phone (< medium) collapses the table (WWW-5152).
      {
        accessor: 'price',
        title: t('price'),
        textAlign: 'right',
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.medium})`,
        render: asset => <PriceColumn price={priceByAssetId?.[asset.asset_id]} />,
      },
      {
        accessor: 'performance',
        title: t('24hChange'),
        textAlign: 'right',
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.medium})`,
        render: asset => <Change24hColumn asset={asset} />,
      },
      // Phone-only: sparkline comes second, before the merged price column.
      {
        accessor: 'sparkline',
        title: interval === '5D' ? t('sparkline5D') : t('sparkline'),
        textAlign: 'right',
        visibleMediaQuery: theme => `(width < ${theme.breakpoints.medium})`,
        render: asset => (
          <SparklineColumn asset={asset} assetIdBySymbol={assetIdBySymbol} interval={interval} />
        ),
      },
      // Phone-only: merged price + change column. The header label ("Vol") is
      // never visible — the thead is hidden below medium (styles.module.css) —
      // but the column keeps a title for the datatable's synthetic accessor.
      {
        accessor: 'priceChange',
        title: t('vol'),
        textAlign: 'right',
        visibleMediaQuery: theme => `(width < ${theme.breakpoints.medium})`,
        render: asset => (
          <PriceWithChangeColumn asset={asset} price={priceByAssetId?.[asset.asset_id]} />
        ),
      },
      // Tablet/desktop: sparkline sits after the price column(s).
      {
        accessor: 'sparklineAfterPrice',
        title: interval === '5D' ? t('sparkline5D') : t('sparkline'),
        textAlign: 'right',
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.medium})`,
        render: asset => (
          <SparklineColumn asset={asset} assetIdBySymbol={assetIdBySymbol} interval={interval} />
        ),
      },
      // Market cap: tablet and desktop share the single-line value (WWW-5152:
      // tablet renders the same table/header as desktop).
      {
        accessor: 'market_cap.amount',
        title: t('marketCap'),
        textAlign: 'right',
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.medium})`,
        render: asset => <MarketCapColumn asset={asset} />,
      },
      // Tablet/desktop: "Trade" text link to signup.
      {
        accessor: 'action',
        title: '',
        textAlign: 'center',
        width: '0%',
        visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.medium})`,
        render: asset => <TradeColumn asset={asset} />,
      },
      // Phone-only: chevron linking to the stock detail page.
      {
        accessor: 'chevron',
        title: '',
        textAlign: 'center',
        width: '0%',
        visibleMediaQuery: theme => `(width < ${theme.breakpoints.medium})`,
        render: asset => <ChevronColumn asset={asset} />,
      },
    ],
    [t, assetIdBySymbol, priceByAssetId, interval],
  )
}
